@if (phase() !== 'idle') {
    <!-- The card is a slim strip until there is something to put in it, so the results below it barely
         move while Iris works. `is-dismissed` folds it away again after a "nothing relevant" ending. -->
    <div
        class="iris-inline-answer my-2"
        tabindex="-1"
        [class.is-open]="isOpen()"
        [class.is-dismissed]="isDismissed()"
        [class.is-quiet]="phase() === 'noAnswer' || phase() === 'failed'"
        [class.is-working]="isWorking()"
        (mouseleave)="clearCitationHighlight()"
        (keydown.escape)="clearCitationHighlight()"
    >
        <!-- Header. While Iris works it also carries the status, set in the answer's own type so the
             card speaks in one voice rather than introducing a label style of its own. -->
        <div class="iris-card-header d-flex align-items-center gap-2">
            <jhi-iris-logo [size]="IrisLogoSize.TEXT" [class.iris-logo-pulse]="isWorking()" [style.filter]="'none'" aria-hidden="true" />
            <span class="iris-card-label fw-bold">Iris</span>
            <span class="iris-ai-badge d-inline-flex align-items-center rounded-pill fw-bold">{{ 'global.search.irisAiBadge' | artemisTranslate }}</span>
            @if (phase() === 'thinking') {
                <span class="iris-strip-status text-truncate" role="status" data-testid="iris-strip-status">{{ stageStatusKey() | artemisTranslate: stageStatusParams() }}</span>
            }
        </div>

        <!-- Body. Collapsed to nothing while the card is a strip; the grid row animates the opening
             without measuring anything in TypeScript. -->
        <div class="iris-card-body-wrapper">
            <div class="iris-card-body">
                @switch (phase()) {
                    @case ('noAnswer') {
                        <div class="iris-quiet-note d-flex align-items-start gap-2" data-testid="iris-no-answer">
                            <fa-icon [icon]="faCircleInfo" class="iris-quiet-icon flex-shrink-0" aria-hidden="true" />
                            <div>
                                <div>{{ 'global.search.irisAnswerNoneRelevant' | artemisTranslate }}</div>
                                <div class="iris-quiet-sub">{{ 'global.search.irisAnswerNoneRelevantHint' | artemisTranslate }}</div>
                            </div>
                        </div>
                    }
                    @case ('failed') {
                        <div class="iris-quiet-note d-flex align-items-start gap-2" data-testid="iris-answer-failed">
                            <fa-icon [icon]="faCircleInfo" class="iris-quiet-icon flex-shrink-0" aria-hidden="true" />
                            <div>
                                <div>{{ 'global.search.irisAnswerFailed' | artemisTranslate }}</div>
                                <button class="iris-retry-btn bg-transparent border-0 fw-medium p-0" type="button" (click)="retry()" data-testid="iris-answer-retry">
                                    {{ 'global.search.irisAnswerRetry' | artemisTranslate }}
                                </button>
                            </div>
                        </div>
                    }
                    @default {
                        <!-- Answer window with optional collapse. The wrapper delegates hover/tap
                             on the inline citation chips rendered inside the markdown innerHTML. -->
                        <div
                            class="iris-answer-region"
                            tabindex="-1"
                            (mouseover)="onAnswerOver($event)"
                            (mouseout)="onAnswerOut($event)"
                            (focus)="onAnswerOver($event)"
                            (blur)="onAnswerOut($event)"
                            (focusin)="onAnswerOver($event)"
                            (focusout)="onAnswerOut($event)"
                            (click)="onAnswerClick($event)"
                            (keydown.escape)="clearCitationHighlight()"
                            (keydown.enter)="onAnswerKeydownActivate($event)"
                            (keydown.space)="onAnswerKeydownActivate($event)"
                        >
                            <!-- While writing, the card holds a fixed height and the text rises inside it, so the
                                 results underneath stay where they are until the answer settles exactly once. -->
                            <div class="iris-answer-window" [class.is-streaming]="isStreaming()">
                                <div
                                    #answerBody
                                    class="iris-answer-text markdown-preview"
                                    [class.is-clamped]="shouldClamp()"
                                    [class.is-expanded]="isOverflowing() && isExpanded()"
                                    [jhiMarkdown]="citationView().html"
                                    (markdownRendered)="onMarkdownRendered()"
                                ></div>
                            </div>
                        </div>

                        <!-- Show more / less — sits below the text, never overlapping. Only ever shown once the
                             answer is settled, since a toggle cannot mean anything while text is still arriving. -->
                        @if (shouldClamp()) {
                            <button class="iris-toggle-btn bg-transparent border-0 fw-medium" type="button" (click)="isExpanded.set(true)">
                                {{ 'global.search.irisAnswerShowMore' | artemisTranslate }} ↓
                            </button>
                        }
                        @if (isSettled() && isOverflowing() && isExpanded()) {
                            <button class="iris-toggle-btn bg-transparent border-0 fw-medium" type="button" (click)="collapse()">
                                {{ 'global.search.irisAnswerShowLess' | artemisTranslate }} ↑
                            </button>
                        }

                        <!-- Source chips — first INITIAL_VISIBLE_SOURCE_COUNT inline; all chips in one wrapping row when expanded -->
                        @if (sources().length > 0 || entitySources().length > 0) {
                            <div class="iris-chips d-flex align-items-center" [class.iris-chips--expanded]="moreOpen()">
                                @for (view of visibleSourceViews(); track $index) {
                                    <a
                                        class="iris-chip d-inline-flex align-items-center text-decoration-none text-nowrap rounded-pill"
                                        [class.iris-chip-lit]="activeCitations().has(view.markerNumber)"
                                        (mouseenter)="setChipHighlight(view.markerNumber)"
                                        [routerLink]="view.source.lectureUnit.link"
                                        [queryParams]="view.source.lectureUnit.queryParams"
                                        [state]="lectureDeepLinkNavigationState"
                                    >
                                        @if (hasCitations()) {
                                            <span class="iris-chip-n flex-shrink-0" data-testid="iris-chip-number">{{ view.markerNumber }}</span>
                                        }
                                        <fa-icon [icon]="SOURCE_ICONS[view.source.lectureUnit.sourceType] ?? faFile" class="iris-chip-icon flex-shrink-0" aria-hidden="true" />
                                        <span class="iris-chip-name text-truncate">{{ view.source.lectureUnit.name }}</span>
                                        @if (view.source.lectureUnit.displayMeta) {
                                            <span class="iris-chip-meta text-body-secondary">· {{ view.source.lectureUnit.displayMeta }}</span>
                                        }
                                    </a>
                                }
                                <!-- Entity sources (course information): few by construction, marker numbers may interleave with the lecture sources' -->
                                @for (chip of entityChipViews(); track $index) {
                                    <button
                                        class="iris-chip d-inline-flex align-items-center text-nowrap rounded-pill"
                                        type="button"
                                        data-testid="iris-entity-chip"
                                        [class.iris-chip-lit]="activeCitations().has(chip.markerNumber)"
                                        (mouseenter)="setChipHighlight(chip.markerNumber)"
                                        (click)="openEntitySource(chip.source)"
                                    >
                                        @if (hasCitations()) {
                                            <span class="iris-chip-n flex-shrink-0" data-testid="iris-chip-number">{{ chip.markerNumber }}</span>
                                        }
                                        <fa-icon [icon]="chip.icon" class="iris-chip-icon flex-shrink-0" [attr.aria-label]="chip.typeLabelKey | artemisTranslate" />
                                        <span class="iris-chip-name text-truncate">{{ chip.source.title }}</span>
                                        @if (chip.source.course?.name) {
                                            <span class="iris-chip-meta text-body-secondary">· {{ chip.source.course?.name }}</span>
                                        }
                                    </button>
                                }
                                @if (sources().length > INITIAL_VISIBLE_SOURCE_COUNT && !moreOpen()) {
                                    <button class="iris-chip iris-more-btn rounded-pill" type="button" (click)="moreOpen.set(true)">
                                        +{{ sources().length - INITIAL_VISIBLE_SOURCE_COUNT }} {{ 'global.search.irisAnswerMore' | artemisTranslate }}
                                    </button>
                                }
                                @if (moreOpen()) {
                                    <button
                                        class="iris-collapse-btn d-inline-flex align-items-center justify-content-center rounded-pill flex-shrink-0"
                                        type="button"
                                        (click)="moreOpen.set(false)"
                                        [attr.aria-label]="'global.search.irisCollapseSources' | artemisTranslate"
                                    >
                                        <fa-icon [icon]="faChevronUp" />
                                    </button>
                                }
                            </div>
                        }
                    }
                }
            </div>
        </div>

        <!-- Footer, right after the sources: where the reader ends up once the answer is read. -->
        @if (canContinueInIris()) {
            <jhi-global-search-iris-handoff [question]="searchQuery()" [answer]="irisResult()?.answer ?? ''" [citedSources]="citedSources()" [runId]="answerRunId()" />
        }

        @if (citationPopover(); as popover) {
            <div class="iris-cite-popover" role="tooltip" data-testid="iris-citation-popover" [style.left.px]="popover.left" [style.top.px]="popover.top">
                <fa-icon
                    [icon]="popover.icon"
                    class="iris-chip-icon flex-shrink-0"
                    [attr.aria-label]="popover.entityTypeKey ? (popover.entityTypeKey | artemisTranslate) : undefined"
                />
                <span class="iris-cite-popover-name text-truncate">{{ popover.name }}</span>
                @if (popover.meta) {
                    <span class="iris-chip-meta text-body-secondary">· {{ popover.meta }}</span>
                }
            </div>
        }
    </div>
}
